import Box, { type BoxProps } from '@liquidweb-dev/undercurrent/Box';
import Button from '@liquidweb-dev/undercurrent/Button';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import { formatCurrency } from '@liquidweb-dev/banana-stand/format';
import PaymentMethodIcon from 'components/standard/PaymentMethodIcon';
import useExpectedBalance from './useExpectedBalance';

const PaymentMethodDetails = ({
	paymentMethod,
	issuer,
	maskedCcNumber,
	ccExpiration,
}: {
	paymentMethod: string | undefined;
	issuer?: string;
	maskedCcNumber?: string;
	ccExpiration?: string;
}) => {
	if (paymentMethod === 'check') {
		return (
			<Box sx={{ display: 'flex', gap: 100, alignItems: 'center' }}>
				<PaymentMethodIcon paymentMethod="check" />
				<Typography variant="title-xs" sx={{ fontWeight: 500 }}>
					Check
				</Typography>
			</Box>
		);
	}

	if (paymentMethod === 'paypal') {
		return (
			<Box sx={{ display: 'flex', gap: 100, alignItems: 'center' }}>
				<PaymentMethodIcon paymentMethod="paypal" />
				<Typography variant="title-xs" sx={{ fontWeight: 500 }}>
					PayPal
				</Typography>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: 'flex',
				gap: 100,
				alignItems: 'center',
				justifyContent: 'space-between',
			}}
		>
			<Box sx={{ display: 'flex', gap: 100, alignItems: 'center' }}>
				<PaymentMethodIcon paymentMethod={paymentMethod} issuer={issuer} />
				<Typography
					variant="title-xs"
					sx={{ fontWeight: 500, minWidth: '56px' }}
				>
					{maskedCcNumber}
				</Typography>
			</Box>

			<Typography
				variant="body-sm"
				sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
			>
				{ccExpiration}
			</Typography>
		</Box>
	);
};

const SummaryItemBox = ({ children, ...rest }: BoxProps) => (
	<Box
		sx={{
			paddingBottom: 150,
			borderBottom: '1px solid',
			borderColor: ({ vars }) => vars.palette.uc.border.weak,
			'&:last-child': {
				paddingBottom: 0,
				borderBottom: 'none',
			},
		}}
		{...rest}
	>
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'center',
				gap: 100,
			}}
		>
			{children}
		</Box>
	</Box>
);

export interface ConfirmPaymentContentProps {
	amount: number;
	isSubmitting?: boolean;
	onSubmit: () => void;
	paymentMethod: string | undefined;
	ccExpiration?: string;
	issuer?: string;
	maskedCcNumber?: string;
}

const ConfirmPaymentContent = ({
	amount,
	isSubmitting,
	onSubmit,
	paymentMethod,
	ccExpiration,
	issuer,
	maskedCcNumber,
}: ConfirmPaymentContentProps) => {
	const { expectedCredit, expectedRemainingBalance, hasExpectedCredit } =
		useExpectedBalance(amount);

	return (
		<Stack
			sx={{
				gap: 300,
			}}
		>
			<Stack sx={{ gap: 150 }}>
				<SummaryItemBox>
					<Typography
						variant="body-md"
						sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
					>
						Method
					</Typography>

					<Box
						sx={{
							paddingX: 150,
							paddingY: 100,
							borderRadius: ({ borderRadius }) => borderRadius.rounded,
							border: '1px solid',
							borderColor: ({ vars }) => vars.palette.uc.border.weak,
						}}
					>
						<PaymentMethodDetails
							paymentMethod={paymentMethod}
							issuer={issuer}
							maskedCcNumber={maskedCcNumber}
							ccExpiration={ccExpiration}
						/>
					</Box>
				</SummaryItemBox>

				<SummaryItemBox>
					<Typography
						variant="body-md"
						sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
					>
						Amount
					</Typography>

					<Typography variant="body-xl" sx={{ fontWeight: 500 }}>
						{formatCurrency(amount)}
					</Typography>
				</SummaryItemBox>

				<SummaryItemBox>
					<Typography
						variant="body-md"
						sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
					>
						Remaining balance
					</Typography>

					<Typography variant="body-xl" sx={{ fontWeight: 500 }}>
						{formatCurrency(expectedRemainingBalance)}
					</Typography>
				</SummaryItemBox>

				{hasExpectedCredit && (
					<SummaryItemBox>
						<Typography
							variant="body-md"
							sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
						>
							Account credit added
						</Typography>

						<Typography variant="body-xl" sx={{ fontWeight: 500 }}>
							{formatCurrency(expectedCredit, { plusSign: true })}
						</Typography>
					</SummaryItemBox>
				)}
			</Stack>

			<Button
				variant="primary"
				size="large"
				color="highlight"
				onClick={onSubmit}
				loading={isSubmitting}
			>
				Pay now
			</Button>
		</Stack>
	);
};

export default ConfirmPaymentContent;
